import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { apiProducts } from '../../api/apiProducts.api';
import DotSpinner from '../../components/shared/DotSpinner';
import { apiPayment } from '../../api/apiPayment.api';
import { toast } from 'react-toastify';
import { openLink } from '../../utils/openLink';
import { useSubscriptionTerms } from '../../components/terms/SubscriptionTerms';
import { useBindCard } from '../../hooks/useBindCard';
import config from '../../config';
import { apiTrials } from '../../api/apiTrials.api';

const CURRENCY_CONFIG = {
  BYN: {
    Icon: ({ className = '' }) => <img src="/icons/BYN.svg" alt="currency" className={`${className}`} />,
    IconSmall: ({ className = '' }) => (
      <img src="/icons/BYN-small.svg" alt="currency" className={`w-[7px] mx-1 ${className}`} />
    ),
  },
  STAR: {
    Icon: ({ className = '' }) => (
      <img src="/icons/tg_star.png" alt="currency" className={`w-[16px] mx-1 ${className}`} />
    ),
    IconSmall: ({ className = '' }) => (
      <img src="/icons/tg_star.svg" alt="currency" className={`w-[10px] mx-1 ${className}`} />
    ),
  },
};

export function SubscriptionTrial() {
  const { trialId } = useParams();

  const { CheckBox, isChecked, onAccept } = useSubscriptionTerms();

  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);

  const bindCard = useBindCard();

  const disabledButton = isLoading || bindCard.isLoading || !isChecked;

  const [payment, setPayment] = useState('BYN');
  const [trial, setTrial] = useState(null);

  const amount = trial?.product.prices.find((p) => p.currency === payment)?.amount || 0;
  const countDaysText = trial?.countDays ? `${trial.countDays} дня подписки Premium за` : '3 дня подписки Premium за';
  const { Icon, IconSmall } = CURRENCY_CONFIG[payment] || CURRENCY_CONFIG.BYN;

  useEffect(() => {
    fetchTrialInitialState();
  }, [trialId]);

  const fetchTrialInitialState = async () => {
    if (!trialId) return;
    setIsLoading(true);
    try {
      await onAccept();
      await apiTrials.getTrialInfo(axiosPrivate, trialId).then((response) => setTrial(response));
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Ошибка при запросе подарка';
      toast.error(message);
      navigate(config.ROUTES.MEET);
    }
    setIsLoading(false);
  };

  const handleStartTrial = async () => {
    const isNideBundCard = await apiPayment
      .list(axiosPrivate)
      .then((response) => !response.items.some((item) => item.isActive));

    if (isNideBundCard) {
      bindCard.handleCreateBindCard();
      return toast.info('Необходимо привязать активную карту');
    }

    try {
      const response = await apiTrials.useTrial(axiosPrivate, trialId, payment);
      toast.success('Пробный период активирован!');
      navigate(config.ROUTES.SUBSCRIPTION.MAIN);
    } catch (error) {
      const message = error?.response?.data?.error || error.message;
      toast.error(message);
      navigate(config.ROUTES.MEET);
    }
  };

  if (isLoading || !trial) {
    return (
      <div className="flex items-center justify-center min-h-[100vh]">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="relative h-full bg-black">
      {/* Фон */}
      <div className="absolute top-0 left-0 right-0">
        <img src="/icons/premium_people.svg" className="w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, black 0%, transparent 100%)' }}
        />
      </div>

      {/* Контент */}
      <div className="relative z-10 h-full px-4 overflow-scroll">
        <button className="absolute top-[21px] right-[16px] p-4 rounded-full bg-white/10" onClick={() => navigate(-1)}>
          <img src="/icons/close.svg" alt="close" />
        </button>

        <div className="pt-28 mb-3">
          <p className="font-raleway text-[40px] font-bold text-white text-center">MYTA PREMIUM</p>
          <p className="font-raleway text-[16px] font-medium text-white text-center w-full">
            C Premium вы найдёте партнёра в два раза быстрее
          </p>
        </div>

        {/* Переключение типа оплаты */}
        <div className="flex items-center justify-between bg-[#022424] px-1.5 py-1 rounded-[400px] font-raleway mb-7 border-2 border-[#233636]">
          <div
            onClick={() => {
              setPayment('BYN');
            }}
            className={` ${payment === 'BYN' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center `}
          >
            <span className={`${payment === 'BYN' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>Деньги</span>
          </div>
          <div
            onClick={() => {
              setPayment('STAR');
            }}
            className={` ${payment === 'STAR' ? 'bg-[#043939]' : 'bg-transparent'} w-1/2 h-[33px] rounded-[400px] flex justify-center items-center`}
          >
            <span className={`${payment === 'STAR' ? 'text-[#A1F69E]' : 'text-white'} text-[14px]`}>
              Telegram звезды
            </span>
          </div>
        </div>

        <div
          className={`font-raleway relative flex flex-col items-center justify-start w-full  text-white border border-[#A1F69E] bg-[#043939] rounded-[16px] p-3`}
        >
          <div className="font-raleway absolute w-max -top-[20px] left-1/2 -translate-x-1/2 bg-[#A1F69E] rounded-full text-black text-sm text-center font-bold px-2 py-1 italic">
            ПОПРОБУЙ БЕСПЛАТНО!
          </div>
          <span className="font-medium text-[22px]">{countDaysText}</span>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-1">
              <span className="font-raleway text-[#A1F69E] text-[38px] font-semibold">0</span>
              <Icon className="w-[22px] h-[24.75px]" />
            </div>
            <span className="font-raleway text-[26px] font-semibold">+</span>
            <div className="flex items-center gap-1">
              <span className="text-[20px] font-medium">дарим</span>
              <span className="text-[32px] font-medium">{trial.bonus.mytaToken}</span>
              <img src="/icons/myta-coin.svg" className="size-[31px]" />
            </div>
          </div>

          <p className="flex items-center justify-center opacity-50 text-xs">
            далее {amount}
            {'  '}
            <IconSmall className="inline-block align-middle" />
            /мес
          </p>
        </div>

        <div className="w-full px-4 mt-4">
          <table className="w-full text-center text-white font-raleway border-separate border-spacing-0">
            <tr>
              <th className="w-[50%]"></th>
              <th className="w-[20%]">FREE</th>
              <th className="w-[30%] text-[#A1F69E]">PREMIUM</th>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">Монеты MYTA</td>
              <td className="border-b border-r border-[#233636] h-[35px]">—</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">
                {trial.bonus.mytaToken}
                <img src="/icons/myta-coin.svg" className="ml-[7px] w-[20px] h-[20px]" />
              </td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">
                Лайк анкеты <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[35px]">70</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">∞</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left">Узнать кто вас лайкнул</td>
              <td className="border-b border-r border-[#233636] h-full">—</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">∞</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">
                Супер-лайк <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[35px]">1</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[35px]">5</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left h-[55px]">
                Вернуть анкету
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[55px]">1</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">5</td>
            </tr>
            <tr className="h-[55px]">
              <td className="border-b border-r border-[#233636] text-left h-[55px]">
                Видео-знакомства
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[55px]">40</td>
              <td className="border-b border-[#233636] flex flex-row items-center justify-center h-[55px]">250</td>
            </tr>
            <tr className="h-[62px]">
              <td className="border-b border-r border-[#233636] h-[62px] text-left">
                Лайк в видео-знакомствах
                <br />
                <span className="text-lightGray">(в день)</span>
              </td>
              <td className="border-b border-r border-[#233636] h-[62px]">4</td>
              <td className="border-b border-[#233636] h-full">15</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-b border-r border-[#233636] text-left h-[35px]">Реклама</td>
              <td className="border-b border-r border-[#233636] h-[35px]">есть</td>
              <td className="border-b border-[#233636] h-[35px]r">—</td>
            </tr>
            <tr className="h-[35px]">
              <td className="border-r border-[#233636] text-left h-[35px]">
                Бонус <span>(подарки)</span>
              </td>
              <td className="border-r border-[#233636] h-[35px]">—</td>
              <td className="border-[#233636] flex flex-row items-center justify-center h-[35px]">4 🎁</td>
            </tr>
          </table>
        </div>

        <p className="mt-[20px] text-center font-normal left-1.4 text-[12px] text-white opacity-70 pb-[220px]">
          Подписка будет продлеваться автоматически, если она не отменена как минимум за 24 часа до окончания текущего
          периода. Подписку можно отменить в любой момент
        </p>

        <div className="fixed bg-black bottom-0 left-0 right-0 px-4 pb-6">
          <Button className="w-full" onClick={handleStartTrial} disabled={disabledButton}>
            {isLoading ? 'Загрузка...' : 'Начать бесплатный период'}
          </Button>
          <CheckBox />
        </div>
      </div>
    </div>
  );
}
